import { useMemo } from "react";
import { ModalProps } from "@chakra-ui/react";
import { AddIcon, CheckCircleIcon } from "@chakra-ui/icons";
import {
  ColumnPinningState,
  createColumnHelper,
  TableMeta,
} from "@tanstack/react-table";
import { compact } from "lodash";
import RetailerDirectoryModal from "components/Shared/RetailerDirectory/Modal";
import {
  Datum,
  DEFAULT_BRAND_COLUMNS,
  DEFAULT_PINNED_COLUMNS,
} from "components/Shared/RetailerDirectory/Table";
import { createTableMetaHelper } from "components/Shared/ChakraReactTable/utils";
import { useCurrentBrandId } from "utils/currentUserHooks";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import { gql } from "__generated__/gql";
import AddRetailerButton, { AddRetailerPlusButton } from "./AddRetailerButton";

const query = gql(/* GraphQL */ `
  query AddBrandRetailerDirectory($brandId: UUID!) {
    brandRetailersList(
      condition: { brandId: $brandId }
      orderBy: [CREATED_AT_ASC]
    ) {
      id
      retailerId
    }
  }
`);

interface Meta extends TableMeta<Datum> {
  addedRetailerIds: UUID[] | null | undefined;
}

const col = createColumnHelper<Datum>();
const metaFor = createTableMetaHelper<Datum, Meta>();

const columns = [
  ...DEFAULT_BRAND_COLUMNS,
  col.display({
    id: "actions",
    cell: (cell) => {
      const { id } = cell.row.original;
      const { addedRetailerIds } = metaFor.cell(cell) || {};
      if (addedRetailerIds?.includes(id)) {
        return <CheckCircleIcon boxSize={6} color="green" />;
      }
      return <AddRetailerPlusButton retailerId={id} />;
    },
    size: 67,
    meta: { props: { textAlign: "center" } },
  }),
];

const pinnedColumns: ColumnPinningState = {
  ...DEFAULT_PINNED_COLUMNS,
  right: ["actions"],
};

type Props = Pick<ModalProps, "isOpen" | "onClose">;

export default function DirectoryModal({ isOpen, onClose }: Props) {
  const brandId = useCurrentBrandId();

  const { data: brandRetailersData } = useBrandQuery(query, {
    skip: !brandId || !isOpen,
    variables: brandId ? { brandId } : undefined,
  });

  const addedRetailerIds = useMemo(
    () =>
      compact(brandRetailersData?.brandRetailersList?.map((r) => r.retailerId)),
    [brandRetailersData],
  );

  const meta = useMemo<Meta>(() => ({ addedRetailerIds }), [addedRetailerIds]);

  return (
    <RetailerDirectoryModal
      {...{ isOpen, onClose, columns, meta, pinnedColumns }}
      renderDrawerFooter={({ retailerId }) => {
        const isAdded = addedRetailerIds.includes(retailerId);
        return (
          <AddRetailerButton
            retailerId={retailerId}
            isDisabled={isAdded}
            colorScheme="pinkButton"
            leftIcon={isAdded ? undefined : <AddIcon boxSize=".9em" />}
          >
            {isAdded ? "Retailer Added" : "Add Retailer"}
          </AddRetailerButton>
        );
      }}
    />
  );
}
